import MenuIcon from '@mui/icons-material/Menu';
import {
  alpha,
  AppBar,
  AppBarProps,
  Box,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  Toolbar,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, LinkProps, useLocation } from 'react-router-dom';

import usePortalConfig from '../../hooks/usePortalConfig/usePortalConfig';
import useRolePermissions from '../../hooks/useRolePermissions/useRolePermissions';

import { Link } from '../../models/CommonModels';

import ClickableIcon from '../Icons/ClickableIcon';
import HelpIcon from '../Icons/HelpIcon';
import { ChevronDownIcon2x } from '../Icons/ChevronDownIcon';

import NotificationsCenter from '../NotificationsCenter/NotificationsCenter';
import ClickableLink from '../Text/ClickableLink';
import MenuWrapper from '../Menu/MenuStyle';
import MenuItemWrapper from '../Dropdown/DropdownStyle';
import DrawerWrapper from '../Drawer/DrawerStyle';
import LanguageSelector from '../LanguageSelector/LanguageSelector';
import NavbarUserMenu from './NavbarUserMenu';
import { Tooltip } from '../StatusTag/StatusTagStyles';

const AppBarWrapper = styled(AppBar)<AppBarProps>(({ theme }) => ({
  top: 0,
  zIndex: theme.zIndex.appBar,
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
  boxShadow: 'none',
  backgroundColor: theme.palette.background.default,
  color: theme.palette.text.primary,
  height: '72px',
  '&.scrolled': {
    boxShadow: '-8px 0px 16px 0px #00000029',
  },
}));

const NavbarButton = styled(RouterLink)<LinkProps>(({ theme }) => ({
  display: 'flex',
  padding: '24px',
  height: '72px',
  justifyContent: 'center',
  alignItems: 'center',
  textTransform: 'none',
  fontSize: '1.5rem',
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: 'normal',
  borderRadius: 0,
  color: theme.palette.text.primary,
  textDecoration: 'none',
  '&.active': {
    borderBottom: `3px solid ${theme.palette.primary.main}`,
    color: theme.palette.primary.main,
  },
  '&:hover': {
    color: theme.palette.primary.main,
    backgroundColor: 'transparent',
  },
}));

const VodafoneIcon = styled('div')`
  min-width: 48px;
  height: 48px;
  background-image: url('/images/vodafone_icon.svg');
  background-size: cover;
`;

interface NavbarProps {
  showNavItems?: boolean;
}
function Navbar({ showNavItems = true }: NavbarProps) {
  const [t] = useTranslation();
  const location = useLocation().pathname;
  const { isUserAllowed } = useRolePermissions();
  const { routePermissions } = usePortalConfig();

  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const navRefs = React.useRef<Record<string, HTMLElement | null>>({});
  const [navMenuAnchorEl, setNavMenuAnchorEl] = React.useState<null | HTMLElement>(null);
  const navMenuOpen = Boolean(navMenuAnchorEl);
  const [navSubLinks, setNavSubLinks] = useState<Link[]>([]);

  useEffect(() => {
    /**
     * Toggle scrolled value
     */
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  /**
   * Set each navbar item ref
   * @param key
   */
  const setNavRef = (key: string) => (el: HTMLElement | null) => {
    navRefs.current[key] = el;
  };

  /**
   * Handles the mouse enter event for the navbar menu
   * @param key
   * @param e
   */
  const handleNavbarMenuClick = (key: string, e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setNavMenuAnchorEl(navRefs.current[key] || e.currentTarget);
  };

  /**
   * Handles the mouse leave event for the dashboard menu
   */
  const handleDashboardClose = () => {
    setNavMenuAnchorEl(null);
  };

  /**
   * Handles the navbar toggle when mobile screen is detected
   */
  const handleDrawerToggle = () => {
    setMobileOpen((prevState) => !prevState);
  };

  const navItems: Link[] = [
    {
      label: t('navbar.dashboard'),
      path: '/dashboard',
      key: 'dashboard',
      allowedRoles: routePermissions.dashboard,
      subLinks: [],
    },
    {
      label: t('navbar.esimManager'),
      path: '/esimManager',
      key: 'esimManager',
      allowedRoles: routePermissions.eSimManager,
      subLinks: [],
    },
    {
      label: t('navbar.companyOnboarding'),
      path: '/companies',
      key: 'companies',
      allowedRoles: routePermissions.companiesList,
      subLinks: [],
    },
    {
      label: t('navbar.uemOnboarding'),
      path: '/uemOnboarding',
      key: 'uemOnboarding',
      allowedRoles: routePermissions.uemList,
      subLinks: [],
    },
    {
      label: t('navbar.adminOnboarding'),
      path: '/admins',
      key: 'admins',
      allowedRoles: routePermissions.adminsList,
      subLinks: [],
    },
    {
      label: t('navbar.import'),
      path: '/import',
      key: 'import',
      allowedRoles: routePermissions.import,
      subLinks: [],
    },
    {
      label: t('navbar.transactions'),
      path: '/transactions',
      key: 'transactions',
      allowedRoles: routePermissions.transactions,
      subLinks: [],
    },
    {
      label: t('navbar.banners'),
      path: '/banners',
      key: 'banners',
      allowedRoles: routePermissions.banners,
      subLinks: [],
    },
  ];

  const renderDrawer = (
    <Box sx={{ textAlign: 'center' }}>
      <Box sx={{ display: 'flex', padding: '0 1rem', alignItems: 'center', height: '72px' }}>
        <VodafoneIcon />
        <Typography sx={{ paddingLeft: '1rem', fontSize: '1.5rem' }}>eSIM Business Manager</Typography>
      </Box>
      <Divider />
      <List>
        {navItems.map((item) =>
          isUserAllowed(item.allowedRoles) && showNavItems ? (
            <React.Fragment key={item.key}>
              <ListItem disablePadding>
                <ListItemButton sx={{ textAlign: 'center' }} onClick={handleDrawerToggle}>
                  <ClickableLink
                    className={location.toLowerCase().includes(item.path.toLowerCase()) ? 'active' : 'teste'}
                    sx={{ fontSize: '1.25rem', textDecoration: 'none', textAlign: 'left', width: '100%' }}
                    to={item.path}
                  >
                    {item.label}
                  </ClickableLink>
                </ListItemButton>
              </ListItem>
              {item.subLinks && item.subLinks.length > 0 ? (
                <List component="div" disablePadding sx={{ backgroundColor: 'transparent' }}>
                  {item.subLinks.map((sub) =>
                    isUserAllowed(sub.allowedRoles) ? (
                      <ListItem key={sub.key} disablePadding>
                        <ListItemButton sx={{ paddingLeft: '2rem' }} onClick={handleDrawerToggle}>
                          <ClickableLink
                            className={location.toLowerCase().includes(sub.path.toLowerCase()) ? 'active' : 'teste'}
                            sx={{
                              fontSize: '1rem',
                              textDecoration: 'none',
                              textAlign: 'left',
                              width: '100%',
                            }}
                            to={sub.path}
                          >
                            {sub.label}
                          </ClickableLink>
                        </ListItemButton>
                      </ListItem>
                    ) : null,
                  )}
                </List>
              ) : null}
            </React.Fragment>
          ) : null,
        )}
      </List>
    </Box>
  );

  return (
    <AppBarWrapper className={isScrolled ? 'scrolled' : ''} position="sticky">
      <Toolbar sx={{ minHeight: '72px !important' }}>
        <IconButton
          color="inherit"
          edge="start"
          onClick={handleDrawerToggle}
          sx={{ mr: { xs: 'none', sm: '2rem' }, display: { md: 'none' } }}
        >
          <MenuIcon />
        </IconButton>
        <VodafoneIcon />
        <Box sx={{ display: { xs: 'none', sm: 'none', md: 'flex' }, paddingLeft: { sm: '50px' } }}>
          {navItems.map((item) =>
            isUserAllowed(item.allowedRoles) && showNavItems ? (
              <NavbarButton
                ref={setNavRef(item.key)}
                key={item.key}
                color="inherit"
                className={location.toLowerCase().includes(item.path.toLowerCase()) ? 'active' : ''}
                onClick={() => {}}
                to={item.path}
                sx={{ fontSize: { md: '1.125rem', lg: '1.5rem' }, padding: { md: '8px', lg: '24px' } }}
              >
                {item.label}
                {item.subLinks && item.subLinks.length > 0 ? (
                  <ClickableIcon
                    onClick={(e) => {
                      setNavSubLinks(item.subLinks || []);
                      handleNavbarMenuClick(item.key, e);
                    }}
                    sx={{
                      marginLeft: '0.5rem',
                      color: 'currentcolor',
                      // the following style is mainly so we have a bigger clickbox for the icon
                      height: '75%',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <ChevronDownIcon2x />
                  </ClickableIcon>
                ) : null}
              </NavbarButton>
            ) : null,
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: '1.25rem', marginLeft: 'auto' }}>
          {showNavItems ? (
            <>
              <LanguageSelector />
              <ClickableIcon onClick={() => {}} sx={{ flexShrink: 0 }}>
                <Tooltip arrow title={t('navbar.helpCentre')}>
                  <RouterLink
                    target="_blank"
                    to={`${import.meta.env.VITE_HELP_SUPPORT_URL}/help/Default_CSH.htm`}
                    style={{ display: 'inline-flex' }}
                  >
                    <HelpIcon />
                  </RouterLink>
                </Tooltip>
              </ClickableIcon>
              <NotificationsCenter />
              <NavbarUserMenu />
            </>
          ) : null}
        </Box>
      </Toolbar>
      <MenuWrapper
        id="navbarItem-menu"
        anchorEl={navMenuAnchorEl}
        open={navMenuOpen}
        onClose={handleDashboardClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        sx={{
          width: '15rem',
        }}
      >
        {navSubLinks.map((item) => (
          <MenuItemWrapper key={item.key} onClick={handleDashboardClose}>
            <ClickableLink
              className={location.toLowerCase().includes(item.path.toLowerCase()) ? 'active' : 'teste'}
              sx={{
                fontSize: '1.125rem',
                textDecoration: 'none',
                textAlign: 'left',
                width: '100%',
              }}
              to={item.path}
            >
              {item.label}
            </ClickableLink>
          </MenuItemWrapper>
        ))}
      </MenuWrapper>
      <DrawerWrapper
        disablePortal
        anchor="left"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: '360px' },
        }}
      >
        {renderDrawer}
      </DrawerWrapper>
    </AppBarWrapper>
  );
}

export default React.memo(Navbar);
